/**
 * Square Appointments integration page.
 *
 * Auth: Square Access Token from Square Developer Dashboard.
 *   Docs: https://developer.squareup.com/docs/devtools/sandbox/overview
 *
 * Capabilities:
 *   - Connect to a Square merchant account (validate token, discover locations)
 *   - Select which location to use for bookings
 *   - Browse bookable services and team members
 *   - Bot workflows use Availability, Book, and Cancel endpoints
 *
 * Bot API reference:
 *   POST /api/admin/Square/Availability — find open slots
 *   POST /api/admin/Square/Book        — create booking
 *   POST /api/admin/Square/Cancel      — cancel booking
 */
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import { PrimaryBlueButton, SecondaryBlueButton, Toggle } from "@velaro/velaro-component-lib";

interface SquareStatus {
  configured: boolean;
  displayName: string;
  merchantName: string;
  locationId: string;
  locationName: string;
  isSandbox: boolean;
}

interface SquareLocation {
  id: string;
  name: string;
  address: string;
}

interface SquareService {
  id: string;
  name: string;
  durationMinutes: number | null;
}

interface SquareTeamMember {
  id: string;
  name: string;
}

export default function SquarePage() {
  const api = useApi();

  const [status, setStatus] = useState<SquareStatus>({
    configured: false,
    displayName: "",
    merchantName: "",
    locationId: "",
    locationName: "",
    isSandbox: false
  });
  const [loading, setLoading] = useState(true);

  // Form state
  const [token, setToken]             = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isSandbox, setIsSandbox]     = useState(false);
  const [saving, setSaving]           = useState(false);
  const [saveError, setSaveError]     = useState<string | null>(null);

  // Location picker
  const [locations, setLocations]         = useState<SquareLocation[]>([]);
  const [selectedLocationId, setSelectedLocationId]     = useState("");
  const [selectedLocationName, setSelectedLocationName] = useState("");
  const [loadingLocations, setLoadingLocations]         = useState(false);

  // Services + team members (for reference)
  const [services, setServices]       = useState<SquareService[]>([]);
  const [teamMembers, setTeamMembers] = useState<SquareTeamMember[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [disconnecting, setDisconnecting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const resp = await api.admin.get("Square/Status");
      const data = await resp.json();
      if (data && typeof data === "object") {
        setStatus(data);
        if (data.configured) {
          setSelectedLocationId(data.locationId ?? "");
          setSelectedLocationName(data.locationName ?? "");
          setDisplayName(data.displayName ?? "");
          setIsSandbox(data.isSandbox ?? false);
        }
      }
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const fetchLocations = useCallback(async () => {
    setLoadingLocations(true);
    try {
      const resp = await api.admin.get("Square/Locations");
      const data = await resp.json();
      setLocations(Array.isArray(data) ? data : []);
    } catch {
      setLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  }, [api.admin]);

  const fetchDetails = useCallback(async () => {
    setLoadingDetails(true);
    try {
      const [svcResp, tmResp] = await Promise.all([
        api.admin.get("Square/Services"),
        api.admin.get("Square/TeamMembers")
      ]);
      const svcData = await svcResp.json();
      const tmData  = await tmResp.json();
      setServices(Array.isArray(svcData) ? svcData : []);
      setTeamMembers(Array.isArray(tmData) ? tmData : []);
    } catch {
      // non-critical
    } finally {
      setLoadingDetails(false);
    }
  }, [api.admin]);

  useEffect(() => {
    if (status.configured) {
      fetchDetails();
    }
  }, [status.configured, fetchDetails]);

  async function handleFetchLocations() {
    if (!token) {
      setSaveError("Please enter your Square Access Token first, then click Fetch Locations.");
      return;
    }
    setSaveError(null);
    // Temporarily save just to fetch — we'll do a full save when user clicks Save Configuration
    setLoadingLocations(true);
    try {
      // Save config first so the server can use the token
      await api.admin.post("Square/Config", {
        displayName: displayName || "My Square Account",
        accessToken: token,
        locationId: "",
        locationName: "",
        isSandbox
      });
      await fetchLocations();
      await fetchStatus();
    } catch {
      setSaveError("Failed to fetch locations. Please check your token.");
    } finally {
      setLoadingLocations(false);
    }
  }

  async function handleSave() {
    if (!token && !status.configured) {
      setSaveError("Please enter your Square Access Token.");
      return;
    }
    if (!selectedLocationId) {
      setSaveError("Please select a location for bookings.");
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const resp = await api.admin.post("Square/Config", {
        displayName: displayName || "My Square Account",
        accessToken: token || undefined,
        locationId: selectedLocationId,
        locationName: selectedLocationName,
        isSandbox
      });
      const data = await resp.json();
      if (!resp.ok) {
        setSaveError(data?.error ?? "Failed to save configuration.");
        return;
      }
      await fetchStatus();
      await fetchDetails();
      setToken(""); // clear token from UI after save
    } catch {
      setSaveError("An unexpected error occurred. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDisconnect() {
    if (!confirm("Disconnect Square? Bot workflows that rely on Square bookings will stop working.")) return;
    setDisconnecting(true);
    try {
      await api.admin.delete("Square/Config");
      setStatus({ configured: false, displayName: "", merchantName: "", locationId: "", locationName: "", isSandbox: false });
      setToken("");
      setLocations([]);
      setServices([]);
      setTeamMembers([]);
      setSelectedLocationId("");
      setSelectedLocationName("");
    } catch {
      // ignore
    } finally {
      setDisconnecting(false);
    }
  }

  if (loading) {
    return <div className="p-8 text-slate-500">Loading Square configuration…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 space-y-8">

      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center text-white font-bold text-lg">S</div>
          <div>
            <h1 className="text-xl font-semibold text-slate-800">Square Appointments</h1>
            <p className="text-sm text-slate-500">Let visitors book services via SMS, WhatsApp, or website chat — powered by your Square calendar.</p>
          </div>
        </div>
      </div>

      {/* Connection status banner */}
      {status.configured && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
          <div>
            <p className="text-green-800 font-medium">Connected to Square</p>
            <p className="text-green-700 text-sm">{status.merchantName} — {status.locationName || "No location selected"}</p>
          </div>
          <button
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="text-red-600 text-sm hover:underline disabled:opacity-50"
          >
            {disconnecting ? "Disconnecting…" : "Disconnect"}
          </button>
        </div>
      )}

      {/* Configuration form */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
        <h2 className="text-base font-semibold text-slate-700">
          {status.configured ? "Update Configuration" : "Connect Square"}
        </h2>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800 space-y-1">
          <p className="font-medium">How to get your Square Access Token:</p>
          <ol className="list-decimal ml-4 space-y-0.5">
            <li>Go to <strong>developer.squareup.com</strong> and sign in</li>
            <li>Open your application (or create one)</li>
            <li>Under <strong>Credentials</strong>, copy the <strong>Access Token</strong> (Production or Sandbox)</li>
            <li>Paste it below and click <strong>Fetch Locations</strong></li>
          </ol>
        </div>

        {/* Display name */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Display name</label>
          <input
            type="text"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g. Downtown Salon"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </div>

        {/* Access token */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Square Access Token {status.configured && <span className="text-slate-400 font-normal">(leave blank to keep existing)</span>}
          </label>
          <input
            type="password"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={status.configured ? "••••••••••••••••" : "EAAAlxxxxxxx…"}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            autoComplete="off"
          />
        </div>

        {/* Sandbox toggle */}
        <div className="flex items-center gap-3">
          <Toggle
            toggled={isSandbox}
            changeToggle={() => setIsSandbox(!isSandbox)}
            label="Sandbox mode (for testing)"
          />
          <span className="text-xs text-slate-500">Use sandbox tokens from Square Developer Dashboard</span>
        </div>

        {/* Fetch locations button */}
        <div>
          <SecondaryBlueButton
            label={loadingLocations ? "Fetching…" : "Fetch Locations"}
            onClick={handleFetchLocations}
            disabled={loadingLocations}
          />
          <p className="text-xs text-slate-500 mt-1">Validates your token and discovers your Square locations.</p>
        </div>

        {/* Location picker */}
        {locations.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Location for bookings</label>
            <select
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedLocationId}
              onChange={(e) => {
                const loc = locations.find((l) => l.id === e.target.value);
                setSelectedLocationId(e.target.value);
                setSelectedLocationName(loc?.name ?? "");
              }}
            >
              <option value="">Select a location…</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}{loc.address ? ` — ${loc.address}` : ""}
                </option>
              ))}
            </select>
          </div>
        )}

        {saveError && (
          <p className="text-sm text-red-600">{saveError}</p>
        )}

        <div className="flex gap-3 pt-2">
          <PrimaryBlueButton
            label={saving ? "Saving…" : "Save Configuration"}
            onClick={handleSave}
            disabled={saving}
          />
        </div>
      </div>

      {/* Services & Team Members (only when configured) */}
      {status.configured && (
        <>
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-base font-semibold text-slate-700 mb-4">Bookable Services</h2>
            {loadingDetails ? (
              <p className="text-sm text-slate-500">Loading services…</p>
            ) : services.length === 0 ? (
              <p className="text-sm text-slate-500">
                No appointment services found. Make sure your Square Catalog has items with type <strong>Appointments service</strong>.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 pr-4 font-medium text-slate-600">Service</th>
                      <th className="text-left py-2 font-medium text-slate-600">Duration</th>
                      <th className="text-left py-2 font-medium text-slate-600">Service ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((svc) => (
                      <tr key={svc.id} className="border-b border-slate-100">
                        <td className="py-2 pr-4 text-slate-800">{svc.name}</td>
                        <td className="py-2 text-slate-600">{svc.durationMinutes ? `${svc.durationMinutes} min` : "—"}</td>
                        <td className="py-2">
                          <code className="text-xs bg-slate-100 rounded px-1.5 py-0.5 text-slate-700">{svc.id}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-base font-semibold text-slate-700 mb-4">Staff (Team Members)</h2>
            {loadingDetails ? (
              <p className="text-sm text-slate-500">Loading team members…</p>
            ) : teamMembers.length === 0 ? (
              <p className="text-sm text-slate-500">No active team members found at this location.</p>
            ) : (
              <div className="space-y-2">
                {teamMembers.map((tm) => (
                  <div key={tm.id} className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <span className="text-slate-800">{tm.name}</span>
                    <code className="text-xs bg-slate-100 rounded px-1.5 py-0.5 text-slate-700">{tm.id}</code>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bot API Reference */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <h2 className="text-base font-semibold text-slate-700">Bot API Reference</h2>
            <p className="text-sm text-slate-500">
              Use these endpoints in <strong>HTTP Request</strong> nodes in your bot workflows.
              All calls require the admin API key in the Authorization header (same as all other admin endpoints).
            </p>

            <div className="space-y-3">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Check availability</p>
                <pre className="bg-slate-800 text-green-300 text-xs rounded-lg p-3 overflow-x-auto">{`POST /api/admin/Square/Availability
{
  "serviceVariationId": "<service-id>",
  "teamMemberId": "<team-member-id>",   // optional
  "startAt": "2026-04-01T00:00:00Z",
  "endAt": "2026-04-08T00:00:00Z"
}`}</pre>
              </div>

              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Book a slot</p>
                <pre className="bg-slate-800 text-green-300 text-xs rounded-lg p-3 overflow-x-auto">{`POST /api/admin/Square/Book
{
  "visitorName": "<<visitorName>>",
  "visitorEmail": "<<visitorEmail>>",
  "visitorPhone": "<<visitorPhone>>",
  "serviceVariationId": "<service-id>",
  "teamMemberId": "<team-member-id>",   // optional
  "startAt": "<<selectedSlot>>",
  "durationMinutes": 60,
  "conversationId": "<<conversationId>>"
}
// Response: { bookingId, confirmationMessage }`}</pre>
              </div>

              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Cancel a booking</p>
                <pre className="bg-slate-800 text-green-300 text-xs rounded-lg p-3 overflow-x-auto">{`POST /api/admin/Square/Cancel
{
  "bookingId": "<<bookingEventId>>"
}`}</pre>
              </div>
            </div>
          </div>
        </>
      )}

      {/* How it works */}
      <details className="bg-white border border-slate-200 rounded-xl">
        <summary className="px-6 py-4 text-base font-semibold text-slate-700 cursor-pointer">
          How it works
        </summary>
        <div className="px-6 pb-6 text-sm text-slate-600 space-y-3">
          <p>
            Square Appointments is the booking engine used by salons, spas, fitness studios, and other service businesses.
            Velaro adds a <strong>conversational front door</strong>: visitors book via SMS, WhatsApp, or website chat
            — no forms, no links to click.
          </p>
          <ol className="list-decimal ml-4 space-y-1">
            <li>Visitor texts "I'd like to book a haircut" → bot presents available services</li>
            <li>Visitor picks a service → bot shows open time slots from Square's availability</li>
            <li>Visitor picks a time → bot calls <code>/Square/Book</code>, Square creates the appointment</li>
            <li>Square handles its own reminders, payment collection, and no-show management</li>
            <li>Velaro stores the booking in BookedAppointments for cancellation flows</li>
          </ol>
          <p>
            The workflow bot also uses your real Square calendar — so if an agent has a conflict or Square
            shows no availability, those slots won't be offered to visitors.
          </p>
        </div>
      </details>
    </div>
  );
}
